import {Menu} from '../interface/menu';
import {MenuItemView} from '../interface/menuItemView';
import {CreateMenuRequest} from '../interface/createMenuRequest';
import {UpdateMenuRequest} from '../interface/updateMenuRequest';
import {
  MenuBackendResponse,
  MenuItemBackendResponse,
  BackendMenuRequest,
  BackendMenuItemRequest,
  BackendUpdateMenuRequest
} from './menu.interfaces';

export class MenuMapper {
  static mapBackendResponseToMenu(response: MenuBackendResponse): Menu {
    const linkedUsers = response.linkedUserCardUsernames || response.linked_users || [];

    const menu: Menu = {
      id: response.id,
      menu_name: response.menuName || response.menu_name || '',
      business_name: response.businessName || response.business_name,
      address: response.address,
      linked_users: linkedUsers,
      is_active: response.isActive !== undefined ? response.isActive : response.is_active,
      created_date: response.createdDate || response.created_date,
      allowsMultipleProducts: response.allowsMultipleProducts !== undefined ? response.allowsMultipleProducts : false,
      hasSearchField: response.hasSearchField !== undefined ? response.hasSearchField : false,
      menu_items: response.menuItems ? response.menuItems.map(item => MenuMapper.mapBackendMenuItemToView(item)) : undefined
    };

    return menu;
  }

  static mapBackendMenuItemToView(item: MenuItemBackendResponse): MenuItemView {
    return {
      id: item.id?.toString() || '',
      name: item.name || '',
      description: item.description || '',
      price: item.price || 0,
      image: item.imageUrl || item.image || null,
      hasQuantity: item.hasQuantity || false,
      quantity: item.quantity
    };
  }

  static mapCreateRequestToBackend(menu: CreateMenuRequest): BackendMenuRequest {
    const menuName = typeof menu.menu_name === 'string' ? menu.menu_name : String(menu.menu_name || '');
    if (!menuName || menuName.trim() === '') {
      throw new Error('Menu name is required');
    }

    if (!menu.linked_users || menu.linked_users.length === 0) {
      throw new Error('At least one linked user is required');
    }

    const usernames = menu.linked_users.map((u: string | { name?: string; id?: string }) => {
      if (typeof u === 'string') {
        return u.trim();
      } else if (u && typeof u === 'object') {
        const name = u.name || u.id || u;
        return typeof name === 'string' ? name.trim() : String(name).trim();
      }
      return String(u || '').trim();
    }).filter((u: string) => u && u !== '');

    if (usernames.length === 0) {
      throw new Error('At least one valid linked user is required');
    }

    const backendRequest: BackendMenuRequest = {
      menuName: menuName.trim(),
      linkedUserCardUsernames: usernames,
      menuItems: []
    };

    if (menu.business_name && typeof menu.business_name === 'string' && menu.business_name.trim() !== '') {
      backendRequest.businessName = menu.business_name.trim();
    }

    if (menu.address && typeof menu.address === 'string' && menu.address.trim() !== '') {
      backendRequest.address = menu.address.trim();
    }

    if (!menu.menu_items || menu.menu_items.length === 0) {
      throw new Error('Menu items are required. Menu cannot be created without items.');
    }

    backendRequest.menuItems = menu.menu_items
      .filter((item: MenuItemView) => {
        const hasName = item.name && item.name.trim() !== '';
        if (item.hasQuantity) {
          return hasName && item.quantity && item.quantity.trim() !== '';
        } else {
          return hasName && item.price > 0;
        }
      })
      .map((item: MenuItemView, index: number) => {
        const menuItem: BackendMenuItemRequest = {
          name: item.name.trim(),
          price: item.hasQuantity ? 0 : Number(item.price),
          hasQuantity: item.hasQuantity === true,
          displayOrder: index
        };

        if (item.description && item.description.trim() !== '') {
          menuItem.description = item.description.trim();
        }

        if (item.image && item.image.trim() !== '') {
          menuItem.imageUrl = item.image.trim();
        }

        if (item.hasQuantity) {
          menuItem.quantity = item.quantity ? item.quantity.trim() : '';
        }

        return menuItem;
      });

    if (backendRequest.menuItems.length === 0) {
      throw new Error('Menu items are required. Please add at least one valid menu item with name and price (or quantity for quantity items).');
    }

    return backendRequest;
  }

  static mapUpdateRequestToBackend(menu: UpdateMenuRequest): BackendUpdateMenuRequest {
    const backendRequest: BackendUpdateMenuRequest = {
      menuName: menu.menu_name?.trim() || '',
      linkedUserCardUsernames: menu.linked_users || []
    };

    if (menu.business_name && menu.business_name.trim() !== '') {
      backendRequest.businessName = menu.business_name.trim();
    } else {
      backendRequest.businessName = null;
    }

    if (menu.address && menu.address.trim() !== '') {
      backendRequest.address = menu.address.trim();
    } else {
      backendRequest.address = null;
    }

    if (menu.allowsMultipleProducts !== undefined) {
      backendRequest.allowsMultipleProducts = menu.allowsMultipleProducts;
    }

    if (menu.hasSearchField !== undefined) {
      backendRequest.hasSearchField = menu.hasSearchField;
    }

    if (menu.menu_items && menu.menu_items.length > 0) {
      backendRequest.menuItems = menu.menu_items
        .filter((item: MenuItemView) => {
          const hasName = item.name && item.name.trim() !== '';
          if (item.hasQuantity) {
            return hasName && item.quantity && item.quantity.trim() !== '';
          } else {
            return hasName && item.price > 0;
          }
        })
        .map((item: MenuItemView, index: number): BackendMenuItemRequest => {
          const menuItem: BackendMenuItemRequest = {
            name: item.name.trim(),
            price: item.hasQuantity ? 0 : Number(item.price),
            hasQuantity: item.hasQuantity === true,
            displayOrder: index
          };

          if (item.description && item.description.trim() !== '') {
            menuItem.description = item.description.trim();
          }

          if (item.image && item.image.trim() !== '') {
            menuItem.imageUrl = item.image.trim();
          }

          if (item.hasQuantity) {
            menuItem.quantity = item.quantity ? item.quantity.trim() : '';
          }

          return menuItem;
        });
    }

    return backendRequest;
  }
}
